import { useState } from 'react';
import { Link } from 'react-router-dom';
import { convertToBase, getAccounts, getIbans } from '@/mocks/api';
import { useAsync } from '@/hooks/useAsync';
import { resolveContext } from '@/state/session';
import { db } from '@/mocks/db';
import { CAPABILITIES, resolveCapability } from '@/services/entitlements';
import { MobileHeader } from '@/components/mobile-shell/MobileHeader';
import { Amount, ListRow, SectionHeader, StatusBadge } from '@/components/banking';
import { EmptyState, ErrorState, LoadingRegion, Notice, RowSkeleton, CardSkeleton } from '@/components/feedback';
import { Sheet } from '@/components/feedback/Sheet';
import { formatIban } from '@/services/formatters';

/**
 * Accounts and IBANs.
 *
 * TRACE:TT-R124-01 — the account and IBAN layout is present at /mobile/accounts.
 * TRACE:TT-R124-03 — the IBANs list is viewable and separated from accounts.
 * TRACE:XS-SMEM-WR-035 — balance-holding accounts are treated separately from
 * IBAN / sort-code-and-account-number identifiers; they are different concepts
 * and are never conflated into one list.
 * TRACE:XS-SMEM-TT-R035-D5 — a total value sits at the top, all currencies
 * converted into one chosen default currency.
 * TRACE:XS-SMEM-WR-040 — the conversion rate is labelled as indicative.
 * TRACE:XS-SMEM-TT-R036-D6 — the list pages so it still works with many accounts.
 */

const PAGE_SIZE = 10;

export function AccountsPage() {
  const resolved = resolveContext();
  const companyId = resolved?.company.id ?? null;
  const userId = resolved?.user.id ?? null;

  const [tab, setTab] = useState<'accounts' | 'ibans'>('accounts');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [addOpen, setAddOpen] = useState(false);

  const accounts = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getAccounts(companyId, userId)).data;
  }, [companyId, userId]);

  const ibans = useAsync(async () => {
    if (!companyId || !userId) return [];
    return (await getIbans(companyId, userId)).data;
  }, [companyId, userId]);

  if (!resolved) return null;

  const base = resolved.company.baseCurrency;
  const ctx = resolved.entitlements;
  const canManage = resolveCapability(ctx, CAPABILITIES.accountsManage).allowed;
  const canFx = resolveCapability(ctx, CAPABILITIES.fxView).allowed;
  const flags = db().featureFlags;

  // TRACE:XS-SMEM-TT-R033-D3 — the Exchange control's position is configurable
  // per client through the `exchangePlacement` feature flag, not hard-coded.
  const exchangeInHeader = canFx && flags.exchangePlacement === 'accounts-header';

  const list = accounts.data ?? [];
  const pageItems = list.slice(0, visible);
  const hasMore = list.length > visible;

  // Convert every currency into the base currency for the headline total.
  let convertedTotal = 0;
  let anyUnconvertible = false;
  const usedRates = new Map<string, number>();
  for (const account of list) {
    const converted = convertToBase(account.availableBalance, account.currency, base);
    if (!converted) {
      anyUnconvertible = true;
      continue;
    }
    convertedTotal += converted.minor;
    if (account.currency !== base) usedRates.set(account.currency, converted.rate);
  }

  const currencies = [...new Set(list.map((a) => a.currency))];

  return (
    <>
      <MobileHeader
        title="Accounts"
        root
        trailing={
          <span className="tm-inline">
            {exchangeInHeader ? (
              <Link to="/mobile/fx" className="tm-btn tm-btn--quiet tm-btn--sm">
                Exchange
              </Link>
            ) : null}
            {canManage && flags.addAccountControl ? (
              <button
                type="button"
                className="tm-icon-btn"
                onClick={() => setAddOpen(true)}
                aria-label="Add an account or currency"
              >
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
            ) : null}
          </span>
        }
      />

      <div className="tm-stack">
        {/* Headline converted total. */}
        {accounts.status === 'loading' ? (
          <LoadingRegion label="Loading your accounts">
            <CardSkeleton height={118} />
          </LoadingRegion>
        ) : accounts.status === 'error' ? (
          <ErrorState error={accounts.error} onRetry={accounts.reload} />
        ) : list.length > 0 ? (
          <div className="tm-card tm-card--accent">
            <p className="tm-eyebrow">Total across all currencies</p>
            <Amount minorUnits={convertedTotal} currency={base} size="hero" />
            <p className="tm-muted" style={{ margin: '8px 0 0' }}>
              {usedRates.size > 0
                ? `Converted to ${base} at an indicative rate · ${[...usedRates.entries()]
                    .map(([c, r]) => `1 ${c} = ${r.toFixed(4)} ${base}`)
                    .join(' · ')}`
                : `All balances are in ${base}`}
            </p>
            {anyUnconvertible ? (
              <p className="tm-muted" style={{ margin: '4px 0 0' }}>
                Some balances could not be converted and are excluded from this total.
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Currency tiles.
            TRACE:XS-SMEM-TT-R032-D2 — each currency tile is clickable and opens
            that currency's history plus its account/IBAN details. */}
        {currencies.length > 1 ? (
          <section aria-labelledby="acc-currencies">
            <SectionHeader title="By currency" id="acc-currencies" />
            <div className="tm-tiles">
              {currencies.map((currency) => {
                const total = list
                  .filter((a) => a.currency === currency)
                  .reduce((s, a) => s + a.availableBalance, 0);
                return (
                  <Link
                    key={currency}
                    to={`/mobile/transactions?currency=${currency}`}
                    className="tm-tile"
                    aria-label={`${currency} balance. Open ${currency} history and details.`}
                  >
                    <span className="tm-eyebrow">{currency}</span>
                    <Amount minorUnits={total} currency={currency} size="lg" />
                  </Link>
                );
              })}
            </div>
          </section>
        ) : null}

        {/* Accounts and IBANs are deliberately separate views of separate
            concepts (WR-035), reachable through one tab control. */}
        <div className="tm-tabs" role="tablist" aria-label="Accounts and IBANs">
          <button
            type="button"
            role="tab"
            id="tab-accounts"
            aria-selected={tab === 'accounts'}
            aria-controls="panel-accounts"
            className={`tm-tab${tab === 'accounts' ? ' tm-tab--active' : ''}`}
            onClick={() => setTab('accounts')}
          >
            Accounts
            <span className="tm-visually-hidden"> — balance-holding accounts</span>
          </button>
          <button
            type="button"
            role="tab"
            id="tab-ibans"
            aria-selected={tab === 'ibans'}
            aria-controls="panel-ibans"
            className={`tm-tab${tab === 'ibans' ? ' tm-tab--active' : ''}`}
            onClick={() => setTab('ibans')}
          >
            IBANs
            <span className="tm-visually-hidden"> — payment identifiers</span>
          </button>
        </div>

        {tab === 'accounts' ? (
          <div id="panel-accounts" role="tabpanel" aria-labelledby="tab-accounts">
            {accounts.status === 'loading' ? (
              <LoadingRegion label="Loading accounts">
                <div className="tm-card tm-card--flush">
                  <RowSkeleton count={4} />
                </div>
              </LoadingRegion>
            ) : list.length === 0 ? (
              <EmptyState
                title="No accounts yet"
                body="When an account is opened for your company it will appear here."
              />
            ) : (
              <>
                <div className="tm-card tm-card--flush">
                  <ul className="tm-rows">
                    {pageItems.map((account) => {
                      const accountIbans = (ibans.data ?? []).filter((i) =>
                        i.accountIds.includes(account.id),
                      );
                      return (
                        <li key={account.id}>
                          <ListRow
                            to={`/mobile/accounts/${account.id}`}
                            title={account.displayName}
                            meta={
                              /* TRACE:TT-R124-02 — an account box handles zero,
                                 one or many IBANs without breaking. */
                              `${account.currency} · ${
                                accountIbans.length === 0
                                  ? 'No IBAN'
                                  : accountIbans.length === 1
                                    ? '1 IBAN'
                                    : `${accountIbans.length} IBANs`
                              }`
                            }
                            end={
                              <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                                <Amount
                                  minorUnits={account.availableBalance}
                                  currency={account.currency}
                                />
                                {account.status !== 'active' ? (
                                  <StatusBadge status={account.status} />
                                ) : null}
                              </span>
                            }
                            ariaLabel={`${account.displayName}, ${account.currency}, ${accountIbans.length} IBANs, status ${account.status}`}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
                {hasMore ? (
                  <button
                    type="button"
                    className="tm-btn tm-btn--secondary tm-btn--block"
                    style={{ marginBlockStart: 12 }}
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  >
                    Show more accounts
                    <span className="tm-visually-hidden">
                      , {list.length - visible} remaining
                    </span>
                  </button>
                ) : null}
                <p className="tm-hint" style={{ marginBlockStart: 8 }}>
                  Showing {pageItems.length} of {list.length}
                </p>
              </>
            )}
          </div>
        ) : (
          <div id="panel-ibans" role="tabpanel" aria-labelledby="tab-ibans">
            {ibans.status === 'loading' ? (
              <LoadingRegion label="Loading IBANs">
                <div className="tm-card tm-card--flush">
                  <RowSkeleton count={3} />
                </div>
              </LoadingRegion>
            ) : (ibans.data ?? []).length === 0 ? (
              <EmptyState
                title="No IBANs yet"
                body="An IBAN lets others pay you from outside the UK. None have been issued for your accounts."
              />
            ) : (
              <div className="tm-card tm-card--flush">
                <ul className="tm-rows">
                  {(ibans.data ?? []).map((iban) => (
                    <li key={iban.id}>
                      <ListRow
                        to={`/mobile/accounts/${iban.accountIds[0] ?? ''}`}
                        title={iban.label ?? iban.holderName}
                        meta={formatIban(iban.iban)}
                        end={
                          <span style={{ display: 'grid', gap: 4, justifyItems: 'end' }}>
                            {iban.isVirtual ? (
                              <StatusBadge status="virtual" label="Virtual" tone="info" />
                            ) : null}
                            {iban.status !== 'active' ? (
                              <StatusBadge status={iban.status} />
                            ) : null}
                          </span>
                        }
                        ariaLabel={`${iban.label ?? iban.holderName}, IBAN ${iban.iban}, status ${iban.status}`}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {!exchangeInHeader && canFx ? (
          <Link to="/mobile/fx" className="tm-btn tm-btn--secondary tm-btn--block">
            Exchange currency
          </Link>
        ) : null}
      </div>

      {/* TRACE:XS-SMEM-TT-R034-D4 — add a new account or currency. The employee
          IBAN-ordering variant is an open decision and is stated as such rather
          than being invented here. */}
      <Sheet open={addOpen} onClose={() => setAddOpen(false)} title="Add an account">
        <Notice tone="info" title="This needs the web app">
          Opening a new currency account runs identity and eligibility checks that need the full
          desktop flow.
        </Notice>
        <div className="tm-stack tm-stack--tight" style={{ marginBlockStart: 12 }}>
          <Link
            to="/mobile/continue-on-web?task=exports"
            className="tm-btn tm-btn--block"
            onClick={() => setAddOpen(false)}
          >
            Continue on web
          </Link>
          <button
            type="button"
            className="tm-btn tm-btn--secondary tm-btn--block"
            onClick={() => setAddOpen(false)}
          >
            Cancel
          </button>
        </div>
      </Sheet>
    </>
  );
}
